// src/components/bio/BioInformationForm.js
import React, { useEffect } from 'react';
import { useBioData } from '../../context/BioDataContext';
import SectionHeader from '../common/SectionHeader';
import SectionNavigation from '../common/SectionNavigation';
import LocalitySelector from './LocalitySelector';
// import { useBioFormAnalytics } from '../../hooks/useAnalytics';
import { useFormInteractionTracking } from '../../hooks/useMicroInteractionTracking';

function BioInformationForm({ onNextSection, onPreviousSection, currentSectionIndex, sectionNames }) {
  const { bioData, handleBioChange } = useBioData();
  // const { trackFieldCompletion, trackFormProgress, trackMilestone } = useBioFormAnalytics();
  const { trackFieldInteraction, trackFormSubmission } = useFormInteractionTracking('BioInformationForm');

  // Calculate form completion percentage
  const calculateCompletionPercentage = () => {
    const fields = ['dateOfBirth', 'entryOnDuty', 'currentGrade', 'currentStep', 'locality'];
    const completedFields = fields.filter(field => bioData[field]);
    return Math.round((completedFields.length / fields.length) * 100);
  };

  // Track form progress when bioData changes
  useEffect(() => {
    const completionPercentage = calculateCompletionPercentage();
    const completedFields = Object.keys(bioData).filter(key => bioData[key]);
    
    // trackFormProgress(completionPercentage, completedFields);
    
    if (completionPercentage === 100) {
      // trackMilestone('bio_form_completed');
    }
  }, [bioData]); // removed trackFormProgress, trackMilestone

  // Enhanced handleBioChange with analytics and interaction tracking
  const handleBioChangeWithAnalytics = (field, value) => {
    const oldValue = bioData[field];
    handleBioChange(field, value);
    // trackFieldCompletion(field, value);
    trackFieldInteraction(field, 'change', value);
  };

  // Track field focus events
  const handleFieldFocus = (fieldName) => {
    trackFieldInteraction(fieldName, 'focus');
  };

  return (
    <div className="calculator-section bio-information-form">
      <SectionHeader title="Basic Information" sectionKey="bio" />

      <div className="calculator-form">
        <div className="form-row">
          <div className="form-group compact">
            <label htmlFor="dob-input">Date of Birth:</label>
            <input
              type="date"
              id="dob-input"
              value={bioData.dateOfBirth || ''}
              onChange={(e) => handleBioChangeWithAnalytics('dateOfBirth', e.target.value)}
              onFocus={() => handleFieldFocus('dateOfBirth')}
              className="date-input"
              aria-label="Date of Birth"
              max={new Date().toISOString().split("T")[0]} // Prevent future dates
            />
          </div>
          <div className="form-group compact">
            <label htmlFor="eod-input">Entry on Duty (EOD):</label>
            <input
              type="date"
              id="eod-input"
              value={bioData.entryOnDuty || ''}
              onChange={(e) => handleBioChangeWithAnalytics('entryOnDuty', e.target.value)}
              onFocus={() => handleFieldFocus('entryOnDuty')}
              className="date-input"
              aria-label="Entry on Duty Date"
              max={new Date().toISOString().split("T")[0]} // Prevent future dates
            />
          </div>
          </div>

          <div className="form-row">
            <div className="form-group compact grade-step-container">
              <label htmlFor="grade-select">Current Grade & Step:</label>
              <div className="grade-step-inputs">
                <select
                  id="grade-select"
                  value={bioData.currentGrade || ''}
                  onChange={(e) => handleBioChangeWithAnalytics('currentGrade', parseInt(e.target.value))}
                  className="gs-input"
                  aria-label="Current GS Grade"
                >
                  {[...Array(15)].map((_, i) => (
                    <option key={i+1} value={i+1}>GS-{i+1}</option>
                  ))}
                </select>
                <span className="input-separator">Step</span>
                <select
                  id="step-select"
                  value={bioData.currentStep || ''}
                  onChange={(e) => handleBioChangeWithAnalytics('currentStep', parseInt(e.target.value))}
                  className="gs-input"
                  aria-label="Current GS Step"
                >
                  {[...Array(10)].map((_, i) => (
                    <option key={i+1} value={i+1}>{i+1}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group compact">
              <label htmlFor="locality-select">Locality Pay Area:</label>
              <LocalitySelector
                id="locality-select" // ID for label association
                value={bioData.locality}
                onChange={(value) => handleBioChangeWithAnalytics('locality', value)}
              />
            </div>

            <div className="form-group compact">
              <label htmlFor="tsp-balance-input">Current TSP Balance:</label>
               <div className="input-with-prefix">
                 <span className="input-prefix">$</span>
                 <input
                    type="text"
                    inputMode="numeric"
                    id="tsp-balance-input"
                    value={(bioData.currentTspBalance || 0).toLocaleString('en-US')}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      const numericValue = parseInt(rawValue, 10) || 0;
                      handleBioChangeWithAnalytics('currentTspBalance', numericValue);
                    }}
                    aria-label="Current TSP Balance"
                 />
               </div>
               <span className="help-text">Enter your total current TSP balance across all funds.</span>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group compact">
              <label htmlFor="sick-leave-input">Projected Unused Sick Leave (hours):
                <div className="tooltip" style={{display: 'inline-block', marginLeft: '5px'}}>
                  <span className="tooltip-icon">?</span>
                  <span className="tooltip-text">Unused sick leave at retirement is converted into extra service credit to increase your FERS pension. Every 174 hours adds one month to your service time.</span>
                </div>
              </label>
              <input
                type="number"
                id="sick-leave-input"
                value={bioData.projectedSickLeave === 0 ? '' : (bioData.projectedSickLeave || '')}
                onFocus={(e) => {
                  if (bioData.projectedSickLeave === 0) {
                    e.target.value = '';
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === '') {
                    handleBioChange('projectedSickLeave', 0);
                  }
                }}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0;
                  handleBioChange('projectedSickLeave', value);
                }}
                className="number-input"
                aria-label="Projected Unused Sick Leave Hours"
                min="0"
                max="10000"
                step="1"
              />
            </div>
            
            <div className="form-group compact">
              <label htmlFor="sick-leave-credit">Additional Credited Service:</label>
              <input
                type="text"
                id="sick-leave-credit"
                value={`${(bioData.projectedSickLeave / 2087).toFixed(2)} years`}
                readOnly
                disabled
                className="number-input calculated-field"
                aria-label="Additional Credited Service from Sick Leave"
              />
              <span className="help-text">Years of service added to pension calculation.</span>
            </div>
          </div>

          {/* Bio Information Form - Social Security component moved to its own section */}
        </div>
      <SectionNavigation
        onNextSection={onNextSection}
        onPreviousSection={onPreviousSection}
        currentSectionIndex={currentSectionIndex}
        sectionNames={sectionNames}
      />
    </div>
  );
}

export default BioInformationForm;